/**
 * @Author: linkxiao
 * @Date: 2024-1-5 10:50
 * @Explain: axios - 请求拦截统一处理: 在原有基础上附加请求覆盖(序列化)处理
 */
import { smlMonitor } from '@/utils/smlMonitor';
import axios, { AxiosInstance } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import {
  axiosBaseURL,
  beforeReqTimeMark,
  onResError,
  onResSuccess,
  setReqConfig,
} from './interfaceUtils';
// utils
import { API, isDev } from '@/config';

class AxiosService {
  public axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: isDev ? axiosBaseURL : API.apiHost + axiosBaseURL,
      // timeout: 100 * 1000, // 100S
    });
    this.setInterceptorsReq();
    this.setInterceptorsRes();
  }

  // 存储接口信息
  state: any = {
    // 存储相同URL最新一次请求响应结果
    // latestResData内：key为请求地址url，value为url相应结果response。
    latestResData: {},
  };

  // 添加拦截器
  private setInterceptorsReq = () => {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // 请求前配置 记录请求
        const key = `${config.url}-${uuidv4()}`;
        config['designReportKey'] = key;
        config[beforeReqTimeMark] = new Date().valueOf();
        smlMonitor?.setNetworkInfo(key, true);

        return setReqConfig(config);
      },
      (error) => {
        // 异常时 删除
        smlMonitor?.delNetworkCount(error?.config?.designReportKey);
        console.error('request.use', error);
        return Promise.reject(error);
      },
    );
  };

  private setInterceptorsRes = () => {
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // 完成时 删除
        smlMonitor?.delNetworkCount(response?.config?.designReportKey);

        // 本次请求发起时间戳
        let reqNowTime = response?.config?.headers?.time;
        // 本次请求url
        let reqNowUrl = response?.config?.url?.split('?')?.[0] || '-';
        // 获取被存储相应信息key，即请求url。
        let urlKeys = Object.keys(this.state.latestResData);
        // 获取上次请求url
        let beforeReqUrl = urlKeys.find((i) => i === reqNowUrl);
        // 获取上次请求时间戳
        let beforeTime = beforeReqUrl
          ? this.state.latestResData[beforeReqUrl]?.config?.headers?.time
          : 0;
        /**
         *
         * 对比当前响应是否为未次请求响应结果
         * 根据服务器返回的响应时间差判断:,(如果上一次接口发起请求的时间晚于当前接口发起请求的时间(即:当前发起接口请求返回的信息被上一次请求接口返回数据所覆盖)
         *
         * beforeReqUrl === this.state.reqNowUrl --> 说明：当前请求URL 于 state内记录的请求URL 相同
         * beforeTime > this.state.reqNowTime --> 说明：当前请求时间早于state内记录的请求时间，响应时间晚于state内接口响应时间
         */
        if (
          beforeTime &&
          reqNowTime &&
          beforeReqUrl &&
          reqNowUrl &&
          beforeTime > reqNowTime &&
          beforeReqUrl === reqNowUrl
        ) {
          // 末次请求 为 前次晚归响应内容赋值
          response = this.state.latestResData[reqNowUrl];
        }
        // 记录接口信息
        this.state.latestResData[reqNowUrl] = response;

        return onResSuccess(response);
      },
      (error) => {
        // 异常时 删除
        smlMonitor?.delNetworkCount(error?.config?.designReportKey);

        let config: any = error?.config;
        // 是否弹出error提示框拦截
        let unUseCommonMsg = config?.params?.unUseCommonMsg;
        if (!unUseCommonMsg) onResError(error);
        return Promise.reject(error);
      },
    );
  };
}

export default new AxiosService().axiosInstance;
